import { schema } from 'normalizr';
import { RSAA, createAction } from 'redux-api-middleware';
import get from 'lodash/get';
import { contractSchema } from './schemas';
import { handleResponse, handleError, buildQueryString } from '../utils';

export const FETCH_PARTNER_REQUEST = 'FETCH_PARTNER_REQUEST';
export const FETCH_PARTNER_SUCCESS = 'FETCH_PARTNER_SUCCESS';
export const FETCH_PARTNER_FAILURE = 'FETCH_PARTNER_FAILURE';
export const FETCH_PARTNERS_REQUEST = 'FETCH_PARTNERS_REQUEST';
export const FETCH_PARTNERS_SUCCESS = 'FETCH_PARTNERS_SUCCESS';
export const FETCH_PARTNERS_FAILURE = 'FETCH_PARTNERS_FAILURE';
export const CREATE_PARTNER_REQUEST = 'CREATE_PARTNER_REQUEST';
export const CREATE_PARTNER_SUCCESS = 'CREATE_PARTNER_SUCCESS';
export const CREATE_PARTNER_FAILURE = 'CREATE_PARTNER_FAILURE';
export const PARTNER_PAGE_SIZE = 20;

export const partnerSchema = new schema.Entity(
  'partners',
  {
    contracts: [contractSchema],
  },
  { idAttribute: 'partnerAddress' },
);

/**
 * Fetch a single partner.
 *
 * @param {string} address - Partner address.
 * @param {object} [options] - Options for fetching.
 * @param {boolean} [options.useCache=true] - If `true`, cache won't be checked & the request will be made.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchPartner = (address, options = { useCache: true }) => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/partners/${address}`,
    method: 'GET',
    bailout: store => options.useCache && get(store, `entities.partners.${address}`, false),
    types: [
      { type: FETCH_PARTNER_REQUEST },
      {
        type: FETCH_PARTNER_SUCCESS,
        payload: (action, state, res) => handleResponse(partnerSchema, action, state, res),
      },
      { type: FETCH_PARTNER_FAILURE, payload: handleError },
    ],
  },
});

/**
 * Fetch partners.
 *
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchPartners = ({
  start = 0,
  count = PARTNER_PAGE_SIZE,
  query,
  filterSilo,
} = {}) => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/search/partners?${buildQueryString({
      start,
      count,
      q: query,
      filterSilo,
    })}`,
    method: 'GET',
    types: [
      { type: FETCH_PARTNERS_REQUEST },
      {
        type: FETCH_PARTNERS_SUCCESS,
        payload: (action, state, res) => handleResponse([partnerSchema], action, state, res),
        meta: (action, state, res) => ({
          totalCount: parseInt(res.headers.get('Total-Count'), 10),
        }),
      },
      { type: FETCH_PARTNERS_FAILURE, payload: handleError },
    ],
  },
});

export const createPartner = values =>
  createAction({
    endpoint: `${window.config.apiUrl}/partners`,
    method: 'POST',
    body: JSON.stringify(values),
    types: [
      { type: CREATE_PARTNER_REQUEST },
      {
        type: CREATE_PARTNER_SUCCESS,
        payload: (action, state, res) => handleResponse(partnerSchema, action, state, res),
      },
      {
        type: CREATE_PARTNER_FAILURE,
        payload: handleError,
      },
    ],
  });
